import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HeroSlider from '@/components/HeroSlider';
import ProductCard from '@/components/ProductCard';
import CategoryProductCarousel from '@/components/CategoryProductCarousel';
import { TrustBar } from '@/components/TrustBadges';
import PaymentBadges from '@/components/PaymentBadges';
import api from '@/lib/api';

export default function HomePage() {
    const [categories, setCategories] = useState([]);
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [featured, setFeatured] = useState([]);

    useEffect(() => {
        api.get('/categories').then(({ data }) => setCategories(data)).catch(() => {});
        api.get('/products/home').then(({ data }) => setCategoryProducts(data || [])).catch(() => {});
        api.get('/products?featured=true&limit=10').then(({ data }) => setFeatured(data.products || [])).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* Hero */}
            <HeroSlider />

            {/* Trust Bar */}
            <TrustBar />

            {/* Shop by Category - Jumia-style horizontal scroll */}
            {categories.length > 0 && (
                <section className="py-8 sm:py-10 bg-white" data-testid="categories-section">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-1 h-7 bg-pink-600 rounded-full" />
                                <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Shop by Category</h2>
                            </div>
                            <Link to="/shop" className="text-sm font-body text-pink-600 hover:text-pink-700 font-medium flex items-center gap-1 bg-pink-50 hover:bg-pink-100 px-3 py-1.5 rounded-full transition-colors" data-testid="view-all-categories">
                                View All <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                        <div className="flex gap-3 sm:gap-4 overflow-x-auto hide-scrollbar pb-2 category-scroll">
                            {categories.map(cat => (
                                <Link key={cat.id} to={`/shop/${cat.slug}`}
                                    className="flex-shrink-0 w-[120px] sm:w-[140px] lg:w-[160px] group text-center"
                                    data-testid={`category-card-${cat.slug}`}>
                                    <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-2 border border-gray-100 group-hover:border-pink-200 group-hover:shadow-[0_4px_20px_rgba(219,39,119,0.15)] transition-all duration-300">
                                        <img
                                            src={cat.image}
                                            alt={`${cat.name} - BeautyByChancy skincare category`}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                    </div>
                                    <span className="text-xs sm:text-sm font-body font-semibold text-gray-800 group-hover:text-pink-600 transition-colors leading-tight block px-1">{cat.name}</span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Divider */}
            <div className="h-2 bg-gray-50" />

            {/* Category-based Product Carousels - Jumia style */}
            {categoryProducts.length > 0 && (
                <div className="bg-white" data-testid="category-products-section">
                    {categoryProducts.map(({ category, products }, idx) => (
                        <div key={category.id}>
                            <CategoryProductCarousel category={category} products={products} />
                            {idx < categoryProducts.length - 1 && <div className="max-w-7xl mx-auto px-4"><div className="border-b border-gray-100" /></div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Featured Products Grid */}
            {featured.length > 0 && (
                <section className="py-10 sm:py-14 bg-gray-50" data-testid="featured-section">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-1 h-7 bg-pink-600 rounded-full" />
                                <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Featured Products</h2>
                            </div>
                            <Link to="/shop" className="text-sm font-body text-pink-600 hover:text-pink-700 font-medium flex items-center gap-1 bg-pink-50 hover:bg-pink-100 px-3 py-1.5 rounded-full transition-colors">
                                See All <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5 stagger-children">
                            {featured.map((p, i) => <ProductCard key={p.id} product={p} priority={i < 4} />)}
                        </div>
                    </div>
                </section>
            )}

            {/* Promo Banner */}
            <section className="bg-gradient-to-r from-pink-600 to-pink-700 text-white" data-testid="promo-banner">
                <div className="max-w-7xl mx-auto px-4 py-10 sm:py-14">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                        <div className="text-center sm:text-left">
                            <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
                                <Sparkles className="w-5 h-5 text-pink-200" />
                                <span className="text-xs font-body uppercase tracking-[0.2em] text-pink-200 font-bold">Special Offer</span>
                            </div>
                            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">Ready to Glow?</h2>
                            <p className="font-body text-sm sm:text-base text-pink-100 max-w-lg">
                                Discover premium skincare products that transform your routine. Free delivery on orders above &#8358;50,000 within Abuja.
                            </p>
                        </div>
                        <Link to="/shop">
                            <Button className="bg-white text-pink-600 hover:bg-pink-50 rounded-full px-10 py-3 text-base font-body font-semibold shadow-lg hover:shadow-xl transition-all" data-testid="cta-shop-btn">
                                Shop Now <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Shop With Us */}
            <section className="py-10 sm:py-14 bg-white" data-testid="why-shop-section">
                <div className="max-w-7xl mx-auto px-4">
                    <h2 className="font-heading text-xl sm:text-2xl font-bold text-gray-900 text-center mb-8">Why Shop With BeautyByChancy?</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                            { icon: '01', title: 'Authentic Products Only', desc: 'Every product in our store is 100% original, sourced directly from trusted manufacturers and distributors.' },
                            { icon: '02', title: 'Fast Nationwide Delivery', desc: 'We ship from Abuja to all 36 states + FCT. Same-day delivery available within Abuja.' },
                            { icon: '03', title: 'Expert Beauty Advice', desc: 'Our team provides personalized skincare recommendations. Reach us anytime via WhatsApp.' },
                        ].map(({ icon, title, desc }) => (
                            <div key={icon} className="text-center p-6 rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-[0_4px_20px_rgba(219,39,119,0.08)] transition-all">
                                <div className="w-12 h-12 rounded-full bg-pink-50 flex items-center justify-center mx-auto mb-4">
                                    <span className="text-pink-600 font-heading font-bold text-lg">{icon}</span>
                                </div>
                                <h3 className="font-heading text-base sm:text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                                <p className="text-sm font-body text-gray-500 leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Payment Methods Footer Strip */}
            <div className="bg-gray-50 border-t border-gray-100 py-6">
                <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-3">
                    <span className="text-xs font-body text-gray-400 uppercase tracking-wider">We accept</span>
                    <PaymentBadges />
                </div>
            </div>
        </div>
    );
}
